// Cuentas de un préstamo.
//
// Vivían dentro de la pantalla de Pagos, y la de Clientes hacía su propia
// versión más simple: para decidir si alguien ya había terminado de pagar
// comparaba lo abonado contra el capital, sin contar los intereses. Las dos
// pantallas daban respuestas distintas sobre el mismo préstamo.

// Cuántos cobros caben entre el inicio y el vencimiento, según la frecuencia.
// Sin fecha de vencimiento no hay plazo que repartir, así que se cuenta uno.
export function contarCobros(prestamo) {
  if (!prestamo.maturity_date) return 1;

  const inicio = new Date(prestamo.start_date);
  const fin = new Date(prestamo.maturity_date);
  const dias = Math.round((fin - inicio) / 86400000);
  if (dias <= 0) return 1;

  let cobros;
  switch (prestamo.frequency) {
    case 'daily': cobros = dias; break;
    case 'weekly': cobros = Math.floor(dias / 7); break;
    case 'biweekly': cobros = Math.floor(dias / 15); break;
    default:
      cobros =
        (fin.getFullYear() - inicio.getFullYear()) * 12 +
        (fin.getMonth() - inicio.getMonth()) -
        (fin.getDate() < inicio.getDate() ? 1 : 0);
  }
  return Math.max(1, cobros);
}

// Días que dura cada periodo de cobro.
const DIAS_POR_PERIODO = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };

// ── Cada cuánto se genera el INTERÉS ──
//
// No es lo mismo que cada cuánto paga el cliente, y confundirlo costaba caro.
//
// En el préstamo abierto el interés es SIEMPRE mensual. Lo dijo el
// prestamista: «si yo presto al 7% y me pagan semanal, ese interés no se
// aplica semanal, es mensual; que la persona pague semanal es otra cosa».
//
// Su ejemplo, que es el modelo entero en cuatro renglones: presta 5.000.000 al
// 7% —350.000 de interés al mes—; el cliente le da 500, 300, 200 y 500 mil en
// cuatro semanas; al cerrar el mes eso son 1.500.000, de los cuales 350.000 van
// al interés y 1.150.000 bajan el capital.
//
// El campo `frequency` dice cada cuánto ABONA, y sirve para saber cuándo ir a
// cobrarle y a partir de cuándo está atrasado. No para generar interés. Usarlo
// para las dos cosas le cobraba a un cliente semanal el 7% CADA SEMANA —28% al
// mes— cuando lo pactado era 7% al mes.
//
// La cuota fija es otro negocio y conserva su propio ritmo: ahí cada cuota es
// un vencimiento de verdad, no un corte de interés.
function frecuenciaDelInteres(prestamo) {
  return prestamo && prestamo.modalidad === 'cuota_fija'
    ? prestamo.frequency
    : 'monthly';
}

// Estado completo de un préstamo: cuánto capital queda y cuánto interés se ha
// generado frente a cuánto se ha pagado.
//
// El interés no es un número fijo: se genera cada periodo sobre el capital que
// esté vivo en ese momento. Por eso hay que recorrer la línea de tiempo en
// orden, mezclando los vencimientos de cada periodo con los abonos a capital.
// Calcularlo de otra forma —capital actual por periodos transcurridos— da mal
// en cuanto alguien abona a capital a mitad de camino: cobraría de menos por
// los periodos en que la deuda todavía era alta.
//
// Los abonos parciales salen solos de este modelo. Si el interés del mes son
// 100.000 y el cliente da 50.000 y luego otros 50.000, se suman contra el
// interés generado y el mes queda cubierto. Si solo da 50.000, quedan 50.000
// pendientes y se arrastran.
// Las fechas en que se genera el interés, desde el inicio hasta `hasta`.
//
// Está aparte y exportada para que la lista de próximos cobros use EXACTAMENTE
// las mismas fechas que el cálculo del interés. Si cada una las generara por su
// cuenta, el mismo préstamo diría una cosa en Morosos y otra en la lista de
// cobros, y no habría forma de saber cuál creer.
//
// Los préstamos mensuales van por mes de calendario y no por bloques de 30
// días: si se presta el 15 de enero, el interés se debe el 15 de febrero.
//
// ⚠️ Los que empiezan los días 29, 30 y 31 se desbordan: del 31 de enero,
// `setMonth(+1)` da el 3 de marzo y no el 28 de febrero. El interés del año
// sigue saliendo bien —cada corte se cuenta desde el inicio, así que no se
// acumula— pero las fechas bailan de uno a tres días. Se deja como está a
// propósito: corregirlo cambiaría lo que deben clientes reales en fechas
// concretas.
// El último día de un mes, para no pedir un 31 de febrero.
const ultimoDiaDelMes = (anio, mes) => new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();

export function cortesDePeriodo(prestamo, hasta = new Date()) {
  const inicio = new Date(prestamo.start_date);
  const fin = hasta instanceof Date ? hasta : new Date(hasta);
  if (isNaN(inicio.getTime())) return [];

  const frecInteres = frecuenciaDelInteres(prestamo);
  const diasPeriodo = DIAS_POR_PERIODO[frecInteres] || 30;
  const porMes = frecInteres === 'monthly' || !DIAS_POR_PERIODO[frecInteres];

  // Todo en UTC y recortando el dia al ultimo del mes.
  //
  // Antes se usaba setMonth(), que trabaja en hora local y ademas desborda: del
  // 31 de enero daba el 3 de marzo, no el 28 de febrero. Y como las fechas
  // guardadas ('2026-01-01') se leen como medianoche UTC, en Colombia -cinco
  // horas atras- «el dia 1» era el 31 del mes anterior, asi que los prestamos
  // que empezaban en dia 1 tambien desbordaban.
  //
  // El resultado era que un corte caia dos o tres dias tarde y, durante esos
  // dias, el cliente parecia no deber todavia el mes. Con la liquidacion
  // mensual eso ya no es un detalle: el corte ES el modelo.
  const a = inicio.getUTCFullYear();
  const m = inicio.getUTCMonth();
  const d = inicio.getUTCDate();
  const hora = inicio.getUTCHours();
  const min = inicio.getUTCMinutes();

  const cortes = [];
  const MAX_PERIODOS = 5000; // tope de seguridad para prestamos diarios muy viejos
  for (let n = 1; n <= MAX_PERIODOS; n++) {
    let corte;
    if (porMes) {
      const am = a + Math.floor((m + n) / 12);
      const mm = (m + n) % 12;
      corte = new Date(Date.UTC(am, mm, Math.min(d, ultimoDiaDelMes(am, mm)), hora, min));
    } else {
      corte = new Date(inicio.getTime() + diasPeriodo * n * 86400000);
    }
    if (corte > fin) break;
    cortes.push(corte);
  }
  return cortes;
}

// El corte siguiente a una fecha: cuándo le toca pagar el próximo interés.
export function siguienteCorte(prestamo, desde = new Date()) {
  const ref = desde instanceof Date ? desde : new Date(desde);
  // Se piden los cortes bastante mas alla de la referencia y se toma el primero
  // posterior: asi la regla de las fechas vive en un solo sitio.
  const margen = new Date(ref.getTime() + 400 * 86400000);
  return cortesDePeriodo(prestamo, margen).find(c => c > ref) || null;
}

// ── Liquidación mensual ──────────────────────────────────────────────────────
//
// El modelo que pidió el prestamista: «mi negocio es netamente mensual, y si
// alguien quiere dar diario, semanal o quincenal, que me haga el corte al mes».
//
// Durante el mes los abonos SE ACUMULAN y no mueven la deuda. Al llegar el
// corte:
//
//   1. el interés del mes se calcula sobre lo que debía AL EMPEZAR el mes;
//   2. lo acumulado cubre primero el interés —incluido el que se arrastra—;
//   3. lo que sobre baja la deuda.
//
// Si no alcanza ni para el interés, la deuda NO baja y lo que faltó se suma al
// mes siguiente.
//
// Se diferencia del modelo de siempre en cuándo hace efecto el abono. En el de
// siempre, un abono a capital baja la deuda el mismo día, así que el interés del
// corte se calcula sobre menos plata. Los dos son legítimos; este es el que se
// usa en la calle y es el que se pactó con los clientes.
//
// Aquí el `tipo` del pago no se mira: todo lo que entra es un abono, y el
// reparto lo decide el corte. Es justamente lo que evita tener que clasificar
// cada billete en el momento de recibirlo.
function estadoMensual(prestamo, pagos, hasta) {
  const tasa = (prestamo.interest_rate || 0) / 100;
  const cortes = cortesDePeriodo(prestamo, hasta);

  let saldo = prestamo.amount || 0;
  let arrastre = 0;            // interés que quedó debiendo de meses anteriores
  let interesGenerado = 0;
  let interesPagado = 0;
  let abonadoACapital = 0;
  let desde = new Date(prestamo.start_date);

  // El abono cuenta para el mes que acaba de cerrar si llega dentro de los cinco
  // días siguientes al corte.
  //
  // Cinco lo dijo el prestamista con sus palabras: «hasta 5 días pasados el mes
  // no cobro el mes completo, pero si se pasa sí». No es un número inventado
  // aquí: es su regla de toda la vida, y el programa tiene que hacer lo mismo
  // que él hace de cabeza.
  //
  // Sin esto, el que paga EL DÍA que le toca quedaba fuera: el corte se marca en
  // el primer instante del día y en hora universal, que aquí son las siete de
  // la tarde del día anterior. Al cliente puntual le salía «no se registró
  // ningún abono» y se le arrastraba el interés. En la cartera de un solo
  // prestamista eran 79 abonos.
  // Cinco días son cinco días, en cualquier frecuencia.
  //
  // Se pensó en escalarlos con el periodo —como el anticipo de más abajo— y el
  // prestamista lo corrigió: «si la cuota es para el 15 y la paga el 19, esa
  // cae para la cuota del 15, eso es simple». Y tiene razón, porque las dos
  // reglas miden cosas distintas:
  //
  //   la gracia  mide cuánto SE DEMORÓ la persona en pagar. Es tolerancia
  //              humana, y no depende de si el préstamo es semanal o mensual.
  //   el anticipo mide cuánto del periodo YA SE CONSUMIÓ. Cuatro días de un mes
  //              es casi todo; cuatro días de una semana es la mitad. Ese sí
  //              tiene que ir en proporción.
  //
  // Lo único que hay que impedir es que la ventana se coma el corte siguiente.
  // En un préstamo diario el periodo dura un día: con cinco días de ventana, un
  // abono del 14 se aplicaba al corte del 9 y los cinco cortes de en medio se
  // quedaban vacíos. Por eso se recorta a un día menos que el periodo:
  //
  //     mensual   30 días → 5    la regla, tal cual
  //     quincenal 15     → 5    tal cual
  //     semanal    7     → 5    tal cual
  //     diario     1     → 0    imposible: no hay hueco entre cortes
  const diasDelPeriodo = DIAS_POR_PERIODO[frecuenciaDelInteres(prestamo)] || 30;
  const DIAS_DE_GRACIA = Math.max(0, Math.min(5, diasDelPeriodo - 1));

  // Se comparan DÍAS ENTEROS, no instantes. La fecha de un abono se guarda como
  // medianoche de aquí —las cinco de la mañana universales— y el corte como
  // medianoche universal; comparándolos como momentos, el mismo día parece
  // posterior. Con números de día ese enredo no existe.
  const DIA_MS = 86400000;
  const diaDe = (f) => {
    const d = f instanceof Date ? f : new Date(f);
    return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / DIA_MS);
  };

  // El detalle de cada corte se guarda para poder mostrarlo tal cual: es lo que
  // se le manda al cliente y lo que hay que poder repetir si algún día reclama.
  // No se guarda en la base a propósito —se recalcula de los abonos— para que
  // no existan dos versiones de la misma cuenta.
  const liquidaciones = [];

  // Los tramos encajan sin dejar huecos: cada uno empieza donde acabó el
  // anterior. Al primero no se le corre el borde de abajo, o los abonos de los
  // tres días siguientes al préstamo no caerían en ningún mes.
  //
  // Y el borde va un día ANTES del préstamo, porque hay quien abona el mismo
  // día que se le presta y ese abono tiene que entrar en el primer mes. Con el
  // borde puesto en el día exacto, esos abonos no caían en ningún tramo y
  // desaparecían de la cuenta: a una clienta le borraba 1.500.000 que sí había
  // entregado.
  let dIni = diaDe(desde) - 1;

  for (const corte of cortes) {
    const dFin = diaDe(corte) + DIAS_DE_GRACIA;
    const abonos = pagos.filter(p => {
      const d = diaDe(p.payment_date);
      return d !== null && d > dIni && d <= dFin;
    });
    const acumulado = abonos.reduce((s, p) => s + (p.amount || 0), 0);
    const delMes = saldo * tasa;
    const debido = delMes + arrastre;
    const aInteres = Math.min(acumulado, debido);
    const aCapital = Math.max(0, acumulado - debido);
    const saldoInicial = saldo;

    interesGenerado += delMes;
    interesPagado += aInteres;
    abonadoACapital += Math.min(aCapital, saldo);
    const arrastreAntes = arrastre;
    arrastre = debido - aInteres;
    saldo = Math.max(0, saldo - aCapital);

    liquidaciones.push({
      corte,
      // Desde cuándo corre este mes. Sin esta fecha el estado de cuenta no
      // puede decir «al 10 de julio debía», que es por donde el cliente
      // empieza a leer.
      desde,
      saldoInicial: Math.round(saldoInicial),
      interesDelMes: Math.round(delMes),
      arrastreAnterior: Math.round(arrastreAntes),
      // El `id` viaja para que la pantalla pueda decir a qué mes pertenece cada
      // fila del historial sin volver a calcular la ventana de gracia por su
      // cuenta. Dos cálculos de lo mismo es como se separan con el tiempo.
      abonos: abonos.map(a => ({ id: a.id, fecha: a.payment_date, monto: Math.round(a.amount || 0) })),
      acumulado: Math.round(acumulado),
      aInteres: Math.round(aInteres),
      aCapital: Math.round(aCapital),
      quedaDebiendoInteres: Math.round(arrastre),
      saldoFinal: Math.round(saldo),
    });

    desde = corte;
    dIni = dFin;
  }

  // Lo abonado después del último corte todavía no se ha repartido: está en
  // espera de la próxima liquidación.
  const finalHasta = hasta instanceof Date ? hasta : new Date(hasta);
  const dHasta = diaDe(finalHasta);
  const enEsperaPagos = pagos.filter(p => {
    const d = diaDe(p.payment_date);
    return d !== null && d > dIni && d <= dHasta;
  });
  const enEspera = enEsperaPagos.reduce((s, p) => s + (p.amount || 0), 0);
  // Los mismos abonos con su fecha, para poder nombrarlos uno por uno: «11 ago
  // 2026: $930.000». Una cifra suelta no la reconoce nadie; la fecha sí.
  const abonosEnEspera = enEsperaPagos
    .map(p => ({ id: p.id, fecha: p.payment_date, monto: Math.round(p.amount || 0) }));

  // Cómo va a quedar el próximo corte con lo que hay entregado HOY.
  //
  // Sin esto, al cliente que todavía no ha cerrado su primer mes el estado de
  // cuenta no le decía nada: ni cuánto interés lleva corrido, ni cuánto de lo
  // que entregó va a ir a la deuda. Y ése es justo el que llama a preguntar,
  // porque acaba de empezar y no sabe cómo funciona.
  //
  // Es una proyección, no un hecho: si abona otra vez antes del corte, cambia.
  // Por eso va siempre con la fecha del corte al lado.
  const corteProximo = siguienteCorte(prestamo, finalHasta);
  const interesProximo = saldo * tasa;
  const debidoProximo = interesProximo + arrastre;
  const aInteresProximo = Math.min(enEspera, debidoProximo);
  const aCapitalProximo = Math.max(0, enEspera - debidoProximo);
  const proyeccion = {
    corte: corteProximo,
    desde,
    saldoInicial: Math.round(saldo),
    interesDelMes: Math.round(interesProximo),
    arrastreAnterior: Math.round(arrastre),
    abonos: abonosEnEspera,
    acumulado: Math.round(enEspera),
    aInteres: Math.round(aInteresProximo),
    aCapital: Math.round(aCapitalProximo),
    quedaDebiendoInteres: Math.round(debidoProximo - aInteresProximo),
    saldoFinal: Math.round(Math.max(0, saldo - aCapitalProximo)),
  };

  // En los últimos CUATRO días antes del corte, el mes ya se cobra completo.
  //
  // No son los mismos cinco de la gracia y no tienen por qué serlo: la gracia
  // es lo que el prestamista perdona cuando le pagan tarde; esto es a partir de
  // cuándo da el mes por consumido.
  //
  // Sale de dos casos suyos, resueltos el 11 de agosto de 2026:
  //   · Una clienta pidió 150.000 el 15 de julio al 10% y el 11 de agosto
  //     —cuatro días antes del corte— quiso cancelar. «Hay que cobrarle
  //     45.000»: el mes completo, porque ya está casi consumido.
  //   · Otro cliente pagó el 11 de agosto y quedó debiendo 4.988.400, con su
  //     corte siguiente el 10 de septiembre. «Le cobro esos 4.988.400»: su mes
  //     acaba de arrancar y todavía no se debe.
  //
  // Cobrarlo desde el primer día del mes daba bien el primer caso y mal el
  // segundo. Cobrarlo solo al llegar el corte, al revés.
  // Cuatro salió de medirlo: es el número más pequeño que cumple el caso que él
  // resolvió —a la clienta le faltaban cuatro días y hay que cobrarle el mes—.
  // Con cinco se movían 61 préstamos en vez de 16, sin cambiar ese caso.
  // ⚠️ Los cuatro días son de un periodo de TREINTA. En otras frecuencias hay
  // que escalarlos o el número deja de significar lo mismo:
  //
  //   · En un préstamo DIARIO el periodo dura un día, así que «faltan cuatro
  //     días o menos» es cierto SIEMPRE. Se le sumaba un día de interés de más,
  //     todos los días, para siempre.
  //   · En uno SEMANAL, cuatro días de siete es más de media semana: se cobraba
  //     la semana entera cuando iba por la mitad.
  //
  // Se escala en la misma proporción —4 de 30— y se redondea hacia abajo, que
  // es el lado que no le cobra de más al cliente:
  //
  //     mensual  30 días → 4    (idéntico a antes: la decisión no se toca)
  //     quincenal 15     → 2
  //     semanal    7     → 0    el corte manda, sin anticipo
  //     diario     1     → 0
  //
  // El caso que fijó el cuatro —la clienta a la que le faltaban cuatro días y
  // había que cobrarle el mes— era mensual, y sigue dando igual.
  const DIAS_ANTES_DEL_CORTE = Math.floor(4 * diasDelPeriodo / 30);
  const faltanParaElCorte = diaDe(corteProximo) - dHasta;
  const mesConsumido = DIAS_ANTES_DEL_CORTE > 0
    && faltanParaElCorte <= DIAS_ANTES_DEL_CORTE;
  if (mesConsumido) {
    arrastre += interesProximo;
    interesGenerado += interesProximo;
  }

  // Negativo significa que va adelantado, igual que en el otro modelo.
  const interesPendiente = Math.round(arrastre - enEspera);
  const capitalPendiente = Math.round(saldo);

  return {
    capitalPendiente,
    abonadoACapital: Math.round(abonadoACapital),
    interesPagado: Math.round(interesPagado),
    interesGenerado: Math.round(interesGenerado),
    interesPendiente,
    // El periodo anticipado cuenta también aquí.
    //
    // Si `interesGenerado` lo suma —y lo suma, arriba— pero este número no, la
    // pantalla dice «acumulado de 12 cobros» con la plata de trece. Es un
    // renglón que el prestamista puede dividir con el dedo, y no le daría.
    periodosGenerados: cortes.length + (mesConsumido ? 1 : 0),
    // Con su signo, igual que en el otro modelo: lo abonado después del corte
    // está dentro de `interesPendiente` y tiene que descontarse. Si no, el
    // cliente que abonó ayer sigue viéndose debiendo lo de antes de abonar.
    deudaTotal: Math.max(0, capitalPendiente + interesPendiente),
    aFavor: interesPendiente < 0 ? -interesPendiente : 0,
    interesPorCobro: capitalPendiente * tasa,
    cobros: contarCobros(prestamo),
    abierto: !prestamo.maturity_date,
    saldado: capitalPendiente === 0 && interesPendiente <= 0,
    // Lo que entró y todavía no se reparte, para poder mostrarlo aparte
    enEspera: Math.round(enEspera),
    abonosEnEspera,
    liquidacion: 'mensual',
    // Un renglón por corte, del más viejo al más nuevo
    liquidaciones,
    // Cómo quedará el corte que viene con lo entregado hasta hoy
    proyeccion,
    // Si al corte le faltan cinco días o menos, el interés de ese mes ya está
    // contado en lo que debe. El estado de cuenta lo dice de otra manera.
    mesConsumido,
  };
}

// El préstamo de cuota fija, el del cobradiario.
//
// Es OTRO negocio, no una variante del de Lenin. Aquí se presta 100.000 al 20%
// y el cliente debe 120.000 desde el primer día: no hay corte, no hay interés
// que se genere cada mes, no hay arrastre. Paga 4.000 diarios hasta completar,
// y la deuda es una resta.
//
// El atraso tampoco se mide igual. No es «cuántos días lleva sin pagar» sino
// «cuántas cuotas debería llevar y cuántas lleva»: si va por el día doce debió
// dar doce y dio nueve, está tres cuotas atrás. Es como lo cuenta quien cobra.
//
// Ni una línea de esto toca el cálculo de Lenin: son dos funciones separadas y
// la puerta de entrada las separa en el primer renglón.
function estadoCuotaFija(prestamo, pagos, hasta) {
  const capital = prestamo.amount || 0;
  const tasa = (prestamo.interest_rate || 0) / 100;
  const interesTotal = capital * tasa;
  const total = capital + interesTotal;

  const lista = Array.isArray(pagos) ? pagos : [];
  const abonado = lista.reduce((s, p) => s + (p.amount || 0), 0);
  const debe = Math.max(0, total - abonado);

  // Cada cuota lleva su parte de capital y su parte de interés, en la misma
  // proporción que el préstamo. No se cobra «primero el interés» como en el
  // otro modelo: aquí el interés se pactó entero desde el principio y lo que
  // se está pagando es el total.
  const parteCapital = total > 0 ? capital / total : 1;
  const abonadoACapital = Math.min(capital, abonado * parteCapital);
  const interesPagado = Math.min(interesTotal, abonado - abonadoACapital);

  const cuota = Number(prestamo.cuota) || 0;
  const cuotasTotales = cuota > 0 ? Math.ceil(total / cuota) : 0;
  const cuotasPagadas = cuota > 0 ? Math.floor(abonado / cuota) : 0;

  // Cuántas cuotas debería llevar a día de hoy, contando desde que se prestó.
  const diasPeriodo = DIAS_POR_PERIODO[prestamo.frequency] || 30;
  const inicio = new Date(prestamo.start_date);
  const fin = hasta instanceof Date ? hasta : new Date(hasta);
  const diasCorridos = Math.max(0, Math.floor((fin - inicio) / 86400000));
  const cuotasEsperadas = cuota > 0
    ? Math.min(cuotasTotales, Math.floor(diasCorridos / diasPeriodo))
    : 0;

  // El atraso, en plata y en cuotas. Se compara contra lo que debió haber
  // entregado, no contra la fecha del último abono: quien paga de a poquitos
  // todos los días no está al día si va entregando menos de la cuota.
  const deberiaHaberDado = Math.min(total, cuotasEsperadas * cuota);
  const atrasoEnPlata = Math.max(0, deberiaHaberDado - abonado);
  const cuotasAtrasadas = cuota > 0 ? Math.ceil(atrasoEnPlata / cuota) : 0;

  return {
    modalidad: 'cuota_fija',
    capitalPendiente: Math.round(capital - abonadoACapital),
    abonadoACapital: Math.round(abonadoACapital),
    interesPagado: Math.round(interesPagado),
    interesGenerado: Math.round(interesTotal),
    interesPendiente: Math.round(interesTotal - interesPagado),
    deudaTotal: Math.round(debe),
    // El total pactado, que en este modelo es LA cifra: se dice desde el día uno.
    totalPactado: Math.round(total),
    abonado: Math.round(abonado),
    cuota,
    cuotasTotales,
    cuotasPagadas,
    cuotasEsperadas,
    cuotasAtrasadas,
    atrasoEnPlata: Math.round(atrasoEnPlata),
    aFavor: 0,
    interesPorCobro: cuota,
    cobros: cuotasTotales,
    periodosGenerados: cuotasEsperadas,
    abierto: false,
    saldado: debe <= 0,
    // Los conceptos del otro modelo no existen aquí. Van en cero y no en
    // undefined para que ninguna pantalla se rompa sumando.
    enEspera: 0,
    liquidaciones: [],
    proyeccion: null,
    mesConsumido: false,
  };
}

export function estadoDelPrestamo(prestamo, pagos = [], hasta = new Date()) {
  // La cuota fija es otro negocio y tiene su propia cuenta. Un préstamo que no
  // lo diga no puede entrar aquí ni por error: los que existían antes de que
  // esto se escribiera no lo dicen ninguno.
  if (prestamo && prestamo.modalidad === 'cuota_fija') {
    return estadoCuotaFija(prestamo, pagos, hasta);
  }

  // Los préstamos marcados como mensuales liquidan en el corte. Los demás
  // siguen exactamente como estaban: no se les mueve un peso.
  if (prestamo && prestamo.liquidacion === 'mensual') {
    return estadoMensual(prestamo, Array.isArray(pagos) ? pagos : [], hasta);
  }

  const lista = Array.isArray(pagos) ? pagos : [];
  const tasa = (prestamo.interest_rate || 0) / 100;
  const diasPeriodo = DIAS_POR_PERIODO[prestamo.frequency] || 30;

  const esCapital = (p) => p.tipo === 'capital';
  const interesPagado = lista.filter(p => !esCapital(p)).reduce((s, p) => s + (p.amount || 0), 0);
  const abonadoACapital = lista.filter(esCapital).reduce((s, p) => s + (p.amount || 0), 0);

  const inicio = new Date(prestamo.start_date);
  const fin = hasta instanceof Date ? hasta : new Date(hasta);

  // Los vencimientos de cada periodo desde que empezó hasta hoy. El interés
  // se sigue generando después de la fecha de vencimiento: mientras el cliente
  // tenga el dinero, sigue costándole.
  const eventos = cortesDePeriodo(prestamo, fin).map(fecha => ({ fecha, tipo: 'periodo' }));

  lista.filter(esCapital).forEach(p => {
    const fecha = new Date(p.payment_date);
    if (!isNaN(fecha.getTime())) eventos.push({ fecha, tipo: 'capital', monto: p.amount || 0 });
  });

  // Si coinciden en la misma fecha, primero se genera el interés del periodo y
  // después se aplica el abono: el cliente debe ese periodo completo.
  eventos.sort((a, b) => (a.fecha - b.fecha) || (a.tipo === 'periodo' ? -1 : 1));

  let capital = prestamo.amount || 0;
  let interesGenerado = 0;
  let periodosGenerados = 0;

  eventos.forEach(ev => {
    if (ev.tipo === 'periodo') {
      interesGenerado += capital * tasa;
      periodosGenerados++;
    } else {
      capital = Math.max(0, capital - ev.monto);
    }
  });

  const capitalPendiente = Math.max(0, (prestamo.amount || 0) - abonadoACapital);
  const interesPendiente = Math.round(interesGenerado - interesPagado);

  return {
    capitalPendiente,
    abonadoACapital,
    interesPagado,
    interesGenerado: Math.round(interesGenerado),
    // Negativo significa que va adelantado: pagó interés que aún no se genera.
    interesPendiente,
    periodosGenerados,
    // Lo que debe hoy si liquidara todo.
    //
    // El interés se suma CON su signo. Antes iba envuelto en un `max(0, ...)`
    // que, cuando el cliente venía adelantado, contaba su saldo a favor como
    // cero: la plata que ya había entregado desaparecía de la cuenta y él
    // seguía apareciendo debiéndola. A un cliente le mostraba siete millones de
    // más. Debajo de cero no baja —nadie debe en negativo—; lo que sobra se
    // informa aparte, en `aFavor`.
    deudaTotal: Math.max(0, capitalPendiente + interesPendiente),
    aFavor: interesPendiente < 0 ? -interesPendiente : 0,
    interesPorCobro: capitalPendiente * tasa,
    cobros: contarCobros(prestamo),
    // Préstamo abierto: sin fecha de fin pactada. El interés se genera igual
    // cada periodo, pero no hay plazo que proyectar ni vencimiento que vigilar.
    abierto: !prestamo.maturity_date,
    saldado: capitalPendiente === 0 && interesPendiente <= 0
  };
}

// Cómo se reparte lo que el cliente entrega, entre interés y capital.
//
// Es el orden de siempre en la calle: primero se cubre el interés que se debe,
// y lo que sobra baja el capital. Si el interés está al día, todo baja el
// capital. Quien cobra ya lo hace así de cabeza; el programa se limitaba a
// pedirle que lo hiciera dos veces, una por cada abono.
//
// Ejemplo: debe 200.000 de interés y 500.000 de capital, y entrega 300.000.
// Salen 200.000 al interés y 100.000 al capital.
export function repartirAbono(monto, estado) {
  const entregado = Math.max(0, Math.round(monto || 0));
  // El interés que ya se generó y aún no se ha pagado. Si va adelantado el
  // número es negativo, y entonces no hay interés que cubrir.
  const interesDebido = Math.max(0, Math.round(estado?.interesPendiente || 0));

  const aInteres = Math.min(entregado, interesDebido);
  const aCapital = entregado - aInteres;

  return {
    aInteres,
    aCapital,
    // Cuánto de lo entregado pasa de lo que el cliente debe en total. No se
    // reparte solo: quien cobra tiene que verlo y decidir.
    sobrante: Math.max(0, aCapital - Math.round(estado?.capitalPendiente || 0)),
    // Con el interés al día no hay reparto que explicar: todo va al capital.
    alDiaEnInteres: interesDebido === 0
  };
}

// Reparte un total entre varias partes en proporción a un peso, sin perder ni
// inventar un peso.
//
// Redondeando cada parte por su lado NO suma el total: tres tercios de $100.000
// dan $33.333 × 3 = $99.999, y el peso que falta no queda anotado en ningún
// préstamo. Los sueltos van a quien tenga la fracción más grande, que es como
// se hace un reparto que tiene que cuadrar.
function repartirProporcional(total, pesos) {
  const suma = pesos.reduce((s, p) => s + p, 0);
  if (suma <= 0) return pesos.map((_, i) => (i === 0 ? Math.round(total) : 0));

  const exactos = pesos.map(p => (total * p) / suma);
  const partes = exactos.map(Math.floor);
  let sobran = Math.round(total) - partes.reduce((s, p) => s + p, 0);

  const orden = exactos
    .map((e, i) => ({ i, resto: e - Math.floor(e) }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i);

  for (let k = 0; sobran > 0 && k < orden.length; k++, sobran--) partes[orden[k].i] += 1;
  return partes;
}

// Reparto cuando el cliente tiene VARIOS préstamos y entrega un solo monto.
//
// Es de lo más común aquí: hay clientes que llaman varias veces al mes por
// montos bajos y después mandan una sola plata para todo.
//
// ── Lo que se aprendió probándolo contra el motor ──
//
// EL INTERÉS DE UN PRÉSTAMO QUE SIGUE ABIERTO NO SE PUEDE FORZAR. En los
// préstamos mensuales el motor cobra el mes sobre el saldo al corte y reparte
// él lo que entró, sin mirar si el abono se anotó como «interés» o «capital».
// Anotar $130.000 de interés en un préstamo cuyo mes son $91.000 no cobra
// $130.000: cobra $91.000 y los otros $39.000 se van al capital.
//
// EL INTERÉS DE MÁS SOLO SE PUEDE COBRAR AL CERRAR. Ahí sí: lo que se recibe
// por encima de lo calculado se anota como interés, que es exactamente lo que
// es cuando se cobra el mes completo y el cliente devuelve a los tres días.
//
// Por eso el interés pactado a mano NO se prorratea entre todos. Cada préstamo
// que sigue abierto carga con el suyo —el que el motor va a cobrar de todos
// modos— y la diferencia se reparte entre LOS QUE QUEDAN CERRADOS, que son los
// únicos donde se puede cobrar de verdad.
//
// Con las cifras de Javier Suárez: $850.000, interés pactado $150.000.
//
//     calculado por el motor ... $91.000  (solo el grande tiene mes cumplido)
//     de más ................... $59.000  -> a los dos que se cierran, $29.500
//
//     $100.000 del 17 ago:  $29.500 interés + $100.000 capital = CIERRE
//     $100.000 del 17 ago:  $29.500 interés + $100.000 capital = CIERRE
//     $1.300.000 del 20 jul: $91.000 interés + $500.000 capital -> queda $800.000
//
// Que es, al peso, lo que da haciéndolo a mano con dos cierres y un abono.
//
// EL ORDEN DEL CAPITAL ES DEL MÁS CHICO AL MÁS GRANDE. Antes iba del más viejo,
// con la intención de «cerrar préstamos en vez de dejar tres a medias». La
// intención era la correcta y el orden no la cumplía: cuando el más viejo es
// también el más grande —que es lo normal, se empieza con uno grande y se van
// pidiendo chicos— la plata se la come él entero y no se cierra ninguno. La
// deuda total es la misma caiga donde caiga; lo que cambia es cuántas cuentas
// quedan abiertas.
//
// Empatados de tamaño, primero el más viejo; y si también empatan, por id, para
// que dos personas mirando la misma pantalla vean lo mismo.
//
// `estados` es [{ prestamo, estado }] con el estado ya calculado.
// `opciones.interes`: lo que el prestamista decide cobrar de interés EN TOTAL.
//                     Si no se dice, se cobra el que el programa calculó.
export function repartirEntrePrestamos(monto, estados = [], opciones = {}) {
  const recibido = Math.max(0, Math.round(monto || 0));

  const partes = [...estados]
    .sort((a, b) => new Date(a.prestamo.start_date) - new Date(b.prestamo.start_date))
    .map(e => ({ prestamo: e.prestamo, estado: e.estado, aInteres: 0, aCapital: 0,
                 cierra: false }));

  const suyo = (p) => Math.max(0, Math.round(p.estado.interesPendiente));
  const capitalDe = (p) => Math.max(0, Math.round(p.estado.capitalPendiente));
  const calculado = partes.reduce((s, p) => s + suyo(p), 0);

  const dicho = opciones.interes === undefined || opciones.interes === null
    ? null : Math.max(0, Math.round(Number(opciones.interes) || 0));

  // No se puede cobrar de interés más de lo que entró: los pesos que faltaran
  // saldrían de la nada y el cliente terminaría debiendo MÁS por haber pagado.
  let interesTotal = Math.min(dicho === null ? calculado : dicho, recibido);
  const interesRecortado = dicho !== null && dicho > recibido;

  // El barrido del capital decide qué préstamos se cierran, y eso decide dónde
  // se puede cobrar el interés de más. Por eso va primero.
  const delChicoAlGrande = [...partes].sort((a, b) =>
    (capitalDe(a) - capitalDe(b))
    || (new Date(a.prestamo.start_date || 0) - new Date(b.prestamo.start_date || 0))
    || ((Number(a.prestamo.id) || 0) - (Number(b.prestamo.id) || 0)));

  const barrer = (paraCapital) => {
    let queda = paraCapital;
    for (const p of delChicoAlGrande) {
      const pone = Math.min(Math.max(0, queda), capitalDe(p));
      p.aCapital = pone;
      p.cierra = capitalDe(p) > 0 && pone >= capitalDe(p);
      queda -= pone;
    }
    return Math.max(0, queda);
  };

  let sobrante = barrer(recibido - interesTotal);
  let cerrados = delChicoAlGrande.filter(p => p.cierra);

  // ── Dónde SE PUEDE cobrar interés de más ──
  //
  // No es lo mismo en todos los préstamos, y tratarlos igual costó caro.
  //
  // En los MENSUALES el motor recalcula en el corte y no mira la etiqueta del
  // abono: no hay forma de cobrarles un peso más de su mes. Solo al cerrarlos,
  // porque ahí lo recibido por encima de lo calculado se anota como interés.
  //
  // En los demás —los marcados `inmediato` y los de siempre— la etiqueta SÍ se
  // respeta: un abono anotado como interés cuenta como interés, y adelantar el
  // del mes que viene es legítimo y el motor lo lleva bien.
  //
  // Restringirlo a los que se cierran, como estaba, reventaba el caso de Rubén
  // Sola: préstamo de $2.000.000 `inmediato` más dos chiquitos de $100.000. Al
  // cobrarle los $150.000 del mes, el grande se quedaba en CERO de interés y a
  // los dos chiquitos les caían $75.000 a cada uno —el 75% sobre $100.000—
  // cuando lo suyo eran $10.000. El reparto se veía absurdo y era culpa de la
  // regla, no de las cifras.
  const puedeCobrarDeMas = (p) =>
    p.cierra || (p.prestamo && p.prestamo.liquidacion !== 'mensual');

  let dondeCabe = delChicoAlGrande.filter(puedeCobrarDeMas);

  // El interés de más no tiene dónde cobrarse si NINGÚN préstamo lo admite. Se
  // recorta al calculado y esa plata baja capital, que es lo único que se puede
  // hacer con ella sin inventar una deuda. Se dice, no se hace callando.
  const extraPedido = Math.max(0, interesTotal - calculado);
  const extraSinDondeCobrar = (extraPedido > 0 && dondeCabe.length === 0) ? extraPedido : 0;
  if (extraSinDondeCobrar > 0) {
    interesTotal = Math.min(calculado, recibido);
    sobrante = barrer(recibido - interesTotal);
    cerrados = delChicoAlGrande.filter(p => p.cierra);
    dondeCabe = delChicoAlGrande.filter(puedeCobrarDeMas);
  }

  // Cada préstamo carga con SU interés, del más viejo al más nuevo, hasta donde
  // alcance. Es lo que el motor va a cobrar de todos modos.
  let porRepartir = Math.min(interesTotal, calculado);
  for (const p of partes) {
    const pone = Math.min(Math.max(0, porRepartir), suyo(p));
    p.aInteres = pone;
    porRepartir -= pone;
  }

  // Y el de más, entre los que lo admiten. En proporción a lo que GENERA cada
  // uno —capital por tasa— y no a su capital pelado: dos préstamos del mismo
  // capital a tasas distintas no generan el mismo interés.
  //
  // Con las cifras de Rubén Sola, $150.000 sobre $1.300.000 + $100.000 +
  // $100.000, todos al 10%, esto reparte $130.000 / $10.000 / $10.000: el mes
  // exacto de cada uno, que es lo que el prestamista tenía en la cabeza.
  const extra = Math.max(0, interesTotal - calculado);
  if (extra > 0 && dondeCabe.length > 0) {
    const pesos = dondeCabe.map(p =>
      capitalDe(p) * (Number(p.prestamo.interest_rate) || 1) || capitalDe(p) || 1);
    const trozos = repartirProporcional(extra, pesos);
    dondeCabe.forEach((p, i) => { p.aInteres += trozos[i]; });
  }

  return {
    // En el orden del barrido: es el orden en que hay que leerlo para entender
    // por qué se cerraron unos y otros no.
    partes: delChicoAlGrande.filter(p => p.aInteres > 0 || p.aCapital > 0),
    // Lo que no cabe en ninguna deuda. No se reparte solo: hay que verlo.
    sobrante,
    interesTotal,
    interesCalculado: calculado,
    interesRecortado,
    // Interés que se pidió cobrar y no se pudo, por no cerrarse ningún préstamo.
    extraSinDondeCobrar,
    aCapital: recibido - interesTotal - sobrante,
    capitalDespues: partes.reduce((s, p) => s + (capitalDe(p) - p.aCapital), 0),
    // Cuántos préstamos quedarían saldados con este pago. Se cierran de verdad
    // —por el camino del cierre, no por un abono— o no quedarían cerrados.
    saldados: cerrados.length,
  };
}

// Lo que costaría el préstamo si nunca se abona al capital. Se usa para las
// cuentas de conjunto —cuánto debe un cliente en total— donde no hace falta el
// detalle de cada abono.
export function totalAPagar(prestamo) {
  const interesPorCobro = prestamo.amount * (prestamo.interest_rate / 100);
  return prestamo.amount + interesPorCobro * contarCobros(prestamo);
}

// Resumen de TODO lo que un cliente debe, sumando sus préstamos activos.
//
// Antes se miraba solo el primero de la lista. Con dos préstamos abiertos, si
// el que salía primero estaba saldado, la pantalla daba al cliente por pagado
// y ofrecía borrarlo aunque el otro siguiera vivo.
//
// `pagosPorPrestamo` es un objeto { idDelPrestamo: [pagos] }.
export function deudaDelCliente(prestamos = [], pagosPorPrestamo = {}) {
  // Se apoya en estadoDelPrestamo, no en totalAPagar.
  //
  // Con la fórmula simple, un préstamo abierto contaba un solo cobro de
  // interés: alguien que llevaba doce meses pagando aparecía como que «no debe
  // nada» aunque siguiera debiendo el capital entero. El aviso antes de borrar
  // decía que estaba a paz y salvo con un millón pendiente.
  const estados = prestamos.map(p => estadoDelPrestamo(p, pagosPorPrestamo[p.id] || []));

  const capital = estados.reduce((s, e) => s + e.capitalPendiente, 0);
  const interes = estados.reduce((s, e) => s + Math.max(0, e.interesPendiente), 0);
  const pagado = estados.reduce((s, e) => s + e.interesPagado + e.abonadoACapital, 0);

  return {
    prestamos: prestamos.length,
    capital,
    interes,
    total: capital + interes,
    pagado,
    saldo: capital + interes,
    // Sin préstamos no se afirma que esté pagado: no hay nada que afirmar.
    todoPagado: prestamos.length > 0 && capital + interes === 0
  };
}

// El historial partido por meses, para poder enseñárselo al cliente.
//
// Nace de una pregunta de Carlos que es la de todo prestamista: si ninguna fila
// puede decir «esto fue interés» —porque un solo abono se parte entre las dos
// cosas—, ¿dónde ve el cliente en qué se le fue la plata?
//
// La respuesta es que el reparto existe, pero es del MES, no del abono. Así que
// se dice donde es verdad: un renglón encima de los abonos de cada mes.
//
//     Mes cerrado el 13 ago · abonó $1.050.000
//     $170.100 al interés · $879.900 a la deuda
//
// Devuelve un mapa de `id de abono` → bloque, para que la pantalla solo tenga
// que mirar cuándo cambia. Los abonos que el motor descartó —los que tienen
// fecha anterior al préstamo— quedan fuera del mapa a propósito: siguen
// apareciendo en la tabla, pero sin mes, que es exactamente su situación.
//
// Solo aplica al préstamo abierto. En cuota fija no hay reparto que mostrar y
// en el modelo antiguo cada abono ya se clasifica solo.
export function historialPorMes(prestamo, estado) {
  const vacio = { bloques: [], porAbono: new Map() };
  if (!estado || estado.liquidacion !== 'mensual') return vacio;

  const bloques = [];
  const porAbono = new Map();

  (estado.liquidaciones || []).forEach((liq, i) => {
    const bloque = {
      clave: `mes-${i}`,
      cerrado: true,
      corte: liq.corte,
      desde: liq.desde,
      abonado: liq.acumulado,
      aInteres: liq.aInteres,
      aCapital: liq.aCapital,
      quedaDebiendoInteres: liq.quedaDebiendoInteres,
    };
    bloques.push(bloque);
    (liq.abonos || []).forEach(a => { if (a.id != null) porAbono.set(a.id, bloque); });
  });

  // El mes en curso: lo entregado todavía no se ha repartido, y decirlo importa
  // tanto como el reparto mismo. Si se muestra igual que un mes cerrado, el
  // cliente lo lee como un hecho y después reclama cuando cambie.
  const proy = estado.proyeccion;
  if ((estado.abonosEnEspera || []).length > 0) {
    const bloque = {
      clave: 'en-curso',
      cerrado: false,
      corte: proy && proy.corte,
      desde: proy && proy.desde,
      abonado: estado.enEspera,
      aInteres: proy ? proy.aInteres : null,
      aCapital: proy ? proy.aCapital : null,
    };
    bloques.push(bloque);
    estado.abonosEnEspera.forEach(a => { if (a.id != null) porAbono.set(a.id, bloque); });
  }

  return { bloques, porAbono };
}

// Cómo se llama un abono en la pantalla, según el modelo del préstamo.
//
// El `tipo` que se guarda con cada pago —'interes' o 'capital'— solo significa
// algo en el modelo antiguo. En el préstamo abierto de Lenin el motor **no lo
// mira**: reparte en el corte, primero el interés y el resto a capital (ver el
// comentario de `estadoMensual`). Y en cuota fija no hay reparto que hacer: lo
// que entra es una cuota.
//
// La pantalla, sin embargo, venía escribiendo «💵 Interés» en todas las filas
// porque ese es el valor por defecto de la columna. El resultado es un renglón
// que se contradice con el encabezado que tiene encima: ocho abonos rotulados
// «Interés» sobre un recuadro que dice que $2.449.900 se fueron a capital.
//
// Un prestamista que ve eso no piensa «qué etiqueta tan imprecisa», piensa que
// el programa no sabe en qué se le fue la plata. Así que donde el reparto no lo
// decide el pago, la pantalla deja de afirmar que lo decide.
export function conceptoDelAbono(prestamo, pago) {
  if (prestamo && prestamo.modalidad === 'cuota_fija') {
    return { texto: '🧾 Cuota', color: '#4338ca' };
  }
  if (prestamo && prestamo.liquidacion === 'mensual') {
    return { texto: '💰 Abono', color: '#4338ca' };
  }
  // Modelo antiguo: aquí el tipo sí cambia la cuenta, así que se dice.
  return pago && pago.tipo === 'capital'
    ? { texto: '📉 Abono a capital', color: '#991b1b' }
    : { texto: '💵 Interés', color: '#4338ca' };
}

